import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';

/**
 * AddressAutocomplete (2026-09-22)
 *
 * Champ d'adresse avec suggestions via l'API BAN (Base Adresse Nationale — gouvernement français).
 * API gratuite, sans clé, adaptée aux adresses FR.
 * Doc : https://api-adresse.data.gouv.fr/
 *
 * Props :
 *  - value: string — texte affiché dans l'input
 *  - onChange: (nextValue: string) => void — appelé à chaque frappe (frappe libre autorisée)
 *  - onSelect: ({ street_address, postal_code, city, latitude, longitude }) => void
 *      — appelé quand l'utilisateur choisit une suggestion. Le parent est libre d'en faire ce
 *        qu'il veut (remplir ses autres champs, stocker lat/lng, etc.).
 *  - placeholder, required, className, id, name, testid — pass-through
 *
 * Périmètre strict : aucun autre système métier touché. C'est un simple wrapper autour d'un
 * <Input> qui ajoute une liste déroulante de suggestions au-dessus.
 */
const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder,
  required,
  id,
  name,
  testid,
  className = '',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const skipNextFetchRef = useRef(false);

  useEffect(() => {
    // Debounced fetch on value change
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(value)}&limit=5&autocomplete=1`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('BAN request failed');
        const data = await res.json();
        const items = (data.features || []).map((f) => ({
          label: f.properties?.label || '',
          street_address: [f.properties?.housenumber, f.properties?.street].filter(Boolean).join(' ') || f.properties?.name || '',
          postal_code: f.properties?.postcode || '',
          city: f.properties?.city || '',
          latitude: f.geometry?.coordinates?.[1] ?? null,
          longitude: f.geometry?.coordinates?.[0] ?? null,
        }));
        setSuggestions(items);
        setIsOpen(items.length > 0);
        setHighlightIndex(-1);
      } catch (_err) {
        setSuggestions([]);
        setIsOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  const handleSelect = (item) => {
    skipNextFetchRef.current = true;
    setIsOpen(false);
    setSuggestions([]);
    setHighlightIndex(-1);
    if (onSelect) {
      onSelect({
        street_address: item.street_address || item.label,
        postal_code: item.postal_code,
        city: item.city,
        latitude: item.latitude,
        longitude: item.longitude,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testid ? `${testid}-wrapper` : undefined}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
        <Input
          id={id}
          name={name}
          type="text"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
          placeholder={placeholder}
          required={required}
          className={`pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white h-12 focus:border-[#FFD60A] ${className}`}
          data-testid={testid}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" data-testid={testid ? `${testid}-loader` : undefined} />
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul
          className="absolute z-[1000] top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl max-h-64 overflow-auto"
          data-testid={testid ? `${testid}-suggestions` : 'address-suggestions'}
        >
          {suggestions.map((item, idx) => (
            <li
              key={`${item.label}-${idx}`}
              className={`px-3 py-2.5 cursor-pointer text-sm border-b border-zinc-800 last:border-b-0 ${
                idx === highlightIndex ? 'bg-[#FFD60A]/15 text-[#FFD60A]' : 'text-zinc-200 hover:bg-zinc-800'
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(item)}
              data-testid={testid ? `${testid}-suggestion-${idx}` : `address-suggestion-${idx}`}
            >
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-70" />
                <span className="leading-snug">{item.label}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
